import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CelebrationCue,
  DailyPageView,
  DueActionView,
  DueHabitView,
  IsoDate,
  NoteView,
  ObjectiveSummaryView,
  PriorityView,
  Rating,
  RewardConfig,
  RewardSnapshot,
} from '@contracts';
import { DEFAULT_REWARD_CONFIG } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { PulseLine } from '@ui/shared/primitives/PulseLine';
import { ProgressRing } from '@ui/shared/primitives/ProgressRing';
import { OfflineChip } from '@ui/shared/primitives/OfflineChip';
import { CheckControl } from '@ui/shared/primitives/CheckControl';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { DatePickerPopover } from '@ui/shared/primitives/DatePickerPopover';
import { Plus, MoreHorizontal, CalendarClock } from '@ui/shared/icons';
import { formatKicker, formatRelativeDatePill, localIsoDate } from '@ui/shared/format/date';
import { LevelFooterChip } from '@ui/shared/rewards/LevelFooterChip';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { XPToast } from '@ui/shared/celebrations/XPToast';
import { LevelUpMoment } from '@ui/shared/celebrations/LevelUpMoment';
import { BadgeEarnSheet } from '@ui/shared/celebrations/BadgeEarnSheet';
import { useCelebrationQueue } from '@ui/shared/celebrations/useCelebrationQueue';
import { BuildHabitRow, AvoidHabitRow } from './HabitRows';
import { EmptyToday } from './EmptyToday';
import { MoodEnergyRow } from './MoodEnergyRow';
import { BackfillBanner } from './BackfillBanner';
import { PinnedObjectiveChipStrip } from './PinnedObjectiveChipStrip';
import { SentenceOfDayCardPhone } from './SentenceOfDayCardPhone';
import { MorningNoteSleepCardPhone } from './MorningNoteSleepCardPhone';
import { GratitudeCardPhone } from './GratitudeCardPhone';
import { PrioritiesCardPhone } from './PrioritiesCardPhone';
import { BrainDumpCardPhone } from './BrainDumpCardPhone';
import { FavoriteThingCardPhone } from './FavoriteThingCardPhone';

/**
 * Today view (design/screens/today-view.md v2.2, phase-2b-habits.md SS2). The
 * daily page: due-today actions AND habits share the "Due today" ruled list,
 * avoid habits with remaining allowance get their own "Keeping in check"
 * section after "Next up", one featured next-up action, real reward-engine
 * footer. "The Page" layout + "Surface" percentage ring. All colors route
 * through CSS custom properties from tokens.css — no hard-coded hex here.
 *
 * Density caps (phase-2b-habits.md HB-14, today-view.md v2.2's floor): the
 * merged "Due today" section (dated actions + due build habits) caps at 5
 * visible rows before folding into the header's due-count pill; "Keeping in
 * check" caps at 4 avoid-habit rows before its own "+N more" quiet link.
 */

const DUE_TODAY_ROW_CAP = 5;
const KEEPING_IN_CHECK_ROW_CAP = 4;

export function TodayView({
  onOpenObjective,
  onCreateObjective,
  onOpenRewards,
  onOpenHabits,
  onOpenWeek,
  onOpenObjectives,
  onOpenHistory,
  today: todayOverride,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly onCreateObjective: () => void;
  readonly onOpenRewards: () => void;
  /** Entry point to the habit list screen (phase-2b-habits.md HB-15). */
  readonly onOpenHabits: () => void;
  /**
   * Entry point to the Weekly Review screen (phase-2c-weekly-review.md
   * WR-24). The screen itself is desktop-only (WR-21/WR-22) but this link is
   * reachable from the phone Today view regardless — same navigational-
   * completeness posture as Rewards/Habits' links, not a redesigned mobile
   * layout for the review screen itself.
   */
  readonly onOpenWeek: () => void;
  /** Entry point to the All Objectives overview (phase-2d-management-history.md MC-6). */
  readonly onOpenObjectives: () => void;
  /**
   * Entry point to the Completion History screen (phase-2d-management-history.md
   * MC-15). Desktop-shaped tree only (MC-14) but reachable from here for
   * discoverability, same posture as onOpenWeek above.
   */
  readonly onOpenHistory: () => void;
  /**
   * Test-only override for "today" (defaults to the real system clock).
   *
   * DOMAIN-API GAP (flagged in the final dev-ui report): DomainApi has no
   * "what is today" query — ClockPort is owned by src/domain/src/data and
   * injected only at the composition root (ADR-0001/ADR-0006: domain code
   * never calls Date.now()/new Date() directly; only the injected clock).
   * getDailyPage(date) requires the CALLER to already know today, so this
   * component has no clean way to obtain a date consistent with whatever
   * clock the domain layer is actually using without either this prop or a
   * new domain query — a real fix belongs to the architect (a new DomainApi
   * method), not a unilateral contract edit here. This prop is the smallest
   * fix within dev-ui's own ownership: it lets tests inject a fixed date
   * (matching their fixture's ClockPort) instead of the component silently
   * defaulting to the real system date and drifting out of sync with a
   * fixture clock set to a different day — which is what made this
   * component untestable (and, in one caught case, actually wrong: it was
   * requesting real-system-today while the test's fixture had seeded data
   * against a fixed test date, so nothing matched and every due/next-up
   * assertion failed).
   */
  readonly today?: IsoDate;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [page, setPage] = useState<DailyPageView | null>(null);
  const [offline, setOffline] = useState(false);
  const [rewardConfig, setRewardConfig] = useState<RewardConfig>(DEFAULT_REWARD_CONFIG);
  // Phase 7 (today-phone-parity.md §4): Priorities/Brain-dump/Favorite-thing
  // card data, mirroring TodayView.desktop.tsx's own load() shape for the
  // exact same reads — no new DomainApi surface, see AC-P6.
  const [priorities, setPriorities] = useState<readonly PriorityView[]>([]);
  const [notes, setNotes] = useState<readonly NoteView[]>([]);
  const [objectives, setObjectives] = useState<readonly ObjectiveSummaryView[]>([]);

  const celebration = useCelebrationQueue({
    resolveIdentity: (level) =>
      resolveLevelIdentity({ ...neutralSnapshot(), level, xpTotal: 0 }, rewardConfig).identity,
  });

  const initialToday = todayOverride ?? (localIsoDate() as IsoDate);

  // today-view.md §1.7 (backfill entry point): the date currently being
  // viewed. null means "today" (the common case) rather than duplicating
  // initialToday as the initial value, so "return to today" is always a
  // clean reset to null rather than reassigning a remembered date string.
  const [viewedDate, setViewedDate] = useState<IsoDate | null>(null);
  const activeDate = viewedDate ?? initialToday;
  const isBackfilling = viewedDate !== null;

  const load = useCallback(async () => {
    const [view, config, priorityList, noteList, objectiveList] = await Promise.all([
      api.getDailyPage(activeDate),
      api.getRewardConfig(),
      api.listPriorities(activeDate),
      api.listNotes(),
      api.listObjectives({ includeArchived: false }),
    ]);
    setPage(view);
    setRewardConfig(config);
    setPriorities(priorityList);
    setNotes(noteList);
    setObjectives(objectiveList);
  }, [api, activeDate]);

  useEffect(() => {
    void load();
  }, [load, version]);

  useEffect(() => {
    const status = api.getSyncStatus();
    setOffline(status.state === 'offline_queued');
  }, [api, version]);

  const handleCue = useCallback(
    (cue: CelebrationCue, sourceLabel: string) => {
      if (cue.xpAwarded <= 0 && !cue.levelUp && cue.badgesEarned.length === 0) return;
      // xpTotal-after-award isn't directly on CelebrationCue; the next
      // getRewardSnapshot() read (triggered by useDomainVersion picking up
      // this command's append) is the source of truth, but the level-up
      // moment wants a number to show immediately rather than waiting a
      // tick — fetched eagerly here for that one display purpose only.
      void api.getRewardSnapshot().then((snapshot) => {
        celebration.enqueue(cue, sourceLabel, snapshot.xpTotal);
      });
    },
    [api, celebration],
  );

  if (!page) {
    return (
      <div style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) 0' }}>
        <SkeletonBlock height={80} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={56} />
        <div style={{ height: 'var(--space-3)' }} />
        <SkeletonBlock height={56} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={90} />
      </div>
    );
  }

  // "Due today" = dated at or before today (includes overdue, rendered
  // neutrally per isOverdue — P1-7); everything else (undated or
  // future-dated) is next-up-eligible. Matches the domain layer's
  // pickBestAction/sortDueActions priority exactly (src/domain/today/
  // due-actions.ts) — dueDate <= today is the real due-today boundary, not
  // merely "dueDate is set" (a future-dated next-up action still has a
  // dueDate). Uses `page.date` (the date the domain actually answered with)
  // rather than re-deriving "today" locally — see the domain-API-gap note
  // above `initialToday`.
  const pageDate = page.date;
  const dueTodayActions = page.dueActions.filter(
    (a) => a.dueDate !== null && a.dueDate <= pageDate,
  );
  const nextUp =
    page.dueActions.find((a) => a.dueDate === null || a.dueDate > pageDate) ?? null;

  const buildHabits = page.habits.filter(
    (h): h is Extract<DueHabitView, { habitKind: 'build' }> => h.habitKind === 'build',
  );
  // "Avoid habits with remaining allowance" join the merged Due Today list
  // (phase-2b-habits.md SS2.2 ordering); avoid habits over allowance still
  // render, but only in "Keeping in check" (AC-B11's "reference" state) —
  // see keepingInCheckHabits below, which is ALL avoid habits (both groups
  // render there too, per the row design's allowance-dots/sub-line already
  // being able to show "over" honestly) except the ones already claimed by
  // Due Today, so no avoid habit renders twice.
  const avoidHabitsAll = page.habits.filter(
    (h): h is Extract<DueHabitView, { habitKind: 'avoid' }> => h.habitKind === 'avoid',
  );
  const avoidHabitsWithAllowance = avoidHabitsAll.filter(
    (h) => h.status !== 'pending' || h.allowance.used < h.allowance.limit,
  );

  // Merged "Due today" ordering (AC-B9): dated-today actions first, then due
  // build habits, then due-with-allowance-remaining avoid habits — capped at
  // DUE_TODAY_ROW_CAP total; overflow folds into the header's due-count pill.
  type DueRowItem =
    | { readonly kind: 'action'; readonly action: DueActionView }
    | { readonly kind: 'build-habit'; readonly habit: Extract<DueHabitView, { habitKind: 'build' }> }
    | { readonly kind: 'avoid-habit'; readonly habit: Extract<DueHabitView, { habitKind: 'avoid' }> };

  const dueRowItems: DueRowItem[] = [
    ...dueTodayActions.map((action): DueRowItem => ({ kind: 'action', action })),
    ...buildHabits.map((habit): DueRowItem => ({ kind: 'build-habit', habit })),
    ...avoidHabitsWithAllowance.map((habit): DueRowItem => ({ kind: 'avoid-habit', habit })),
  ];
  const dueRowItemsShown = dueRowItems.slice(0, DUE_TODAY_ROW_CAP);

  // Keeping in check: every avoid habit not already surfaced in the merged
  // Due Today list (i.e. those over allowance, shown here "for reference" —
  // AC-B11) capped at its own row budget with a quiet "+N more" overflow.
  const keepingInCheckHabits = avoidHabitsAll.filter(
    (h) => !avoidHabitsWithAllowance.some((shown) => shown.habitId === h.habitId),
  );
  const keepingInCheckShown = keepingInCheckHabits.slice(0, KEEPING_IN_CHECK_ROW_CAP);
  const keepingInCheckOverflow = keepingInCheckHabits.length - keepingInCheckShown.length;

  const totalDueCount = dueTodayActions.length + buildHabits.length + avoidHabitsWithAllowance.length;
  const nothingAtAll =
    page.dueActions.length === 0 && page.habits.length === 0;

  const celebrationOverlays = (
    <>
      {celebration.xpToast && (
        <XPToast
          amount={celebration.xpToast.amount}
          sourceLabel={celebration.xpToast.sourceLabel}
          onDismiss={celebration.dismissToast}
        />
      )}
      {celebration.levelUp && (
        <LevelUpMoment
          level={celebration.levelUp.level}
          identity={celebration.levelUp.identity}
          xpTotal={celebration.levelUp.xpTotal}
          onDismiss={celebration.dismissLevelUp}
        />
      )}
      {celebration.visibleBadgeId && (
        <BadgeEarnSheetLoader badgeId={celebration.visibleBadgeId} onDismiss={celebration.dismissNextBadge} />
      )}
    </>
  );

  // onToggle handlers are fire-and-forget by contract (ADR-0004: the network
  // is never on the tap path); catch so a rejected promise doesn't become an
  // unhandled rejection. Only a genuine completion (never a revert/undo)
  // triggers a celebration — celebrating an undo would be a false "your
  // effort counted" signal for effort just retracted.
  function toggleAction(action: DueActionView) {
    if (action.status === 'done' && action.entryEventId) {
      api.revertLogEntry(action.entryEventId).catch((error: unknown) => {
        console.error('revert action failed', error);
      });
    } else {
      api
        .completeAction(action.objectiveId, action.actionId, pageDate)
        .then((cue) => handleCue(cue, 'Action done'))
        .catch((error: unknown) => {
          console.error('complete action failed', error);
        });
    }
  }

  function toggleHabitDone(habit: Extract<DueHabitView, { habitKind: 'build' }>) {
    if (habit.status === 'done' && habit.entryEventId) {
      api.revertLogEntry(habit.entryEventId).catch((error: unknown) => {
        console.error('revert habit completion failed', error);
      });
    } else {
      api
        .completeHabit(habit.habitId, pageDate)
        .then((cue) => handleCue(cue, `${habit.name} done`))
        .catch((error: unknown) => {
          console.error('complete habit failed', error);
        });
    }
  }

  function toggleHabitSkip(habit: Extract<DueHabitView, { habitKind: 'build' }>) {
    if (habit.status === 'skipped' && habit.entryEventId) {
      api.revertLogEntry(habit.entryEventId).catch((error: unknown) => {
        console.error('revert habit skip failed', error);
      });
    } else {
      api.skipHabit(habit.habitId, pageDate).catch((error: unknown) => {
        console.error('skip habit failed', error);
      });
    }
  }

  function toggleAvoidClean(habit: Extract<DueHabitView, { habitKind: 'avoid' }>) {
    if (habit.status === 'clean' && habit.entryEventId) {
      api.revertLogEntry(habit.entryEventId).catch((error: unknown) => {
        console.error('revert clean-day log failed', error);
      });
    } else {
      api
        .logClean(habit.habitId, pageDate)
        .then((cue) => handleCue(cue, `${habit.name} clean`))
        .catch((error: unknown) => {
          console.error('log clean day failed', error);
        });
    }
  }

  function logAvoidLapse(habit: Extract<DueHabitView, { habitKind: 'avoid' }>) {
    // Judgment-free, zero confirmation (AC-B12) — fires immediately.
    api.logLapse(habit.habitId, pageDate).catch((error: unknown) => {
      console.error('log lapse failed', error);
    });
  }

  // today-view.md §1.25 (ME-3/AC-P6/AC-P9): re-tapping the already-selected
  // value clears it via the shared revertLogEntry (one-tap-undo) rather than
  // a second control; tapping a different value simply moves the selection
  // (last-tap-wins). No XP/celebration triggered either way (ME-5/AC-P10) —
  // setMood/setEnergy return void by contract, so there is nothing to feed
  // handleCue with.
  function setMoodValue(next: Rating) {
    if (page && page.mood === next && page.moodEventId) {
      api.revertLogEntry(page.moodEventId).catch((error: unknown) => {
        console.error('revert mood failed', error);
      });
    } else {
      api.setMood(pageDate, next).catch((error: unknown) => {
        console.error('set mood failed', error);
      });
    }
  }

  function setEnergyValue(next: Rating) {
    if (page && page.energy === next && page.energyEventId) {
      api.revertLogEntry(page.energyEventId).catch((error: unknown) => {
        console.error('revert energy failed', error);
      });
    } else {
      api.setEnergy(pageDate, next).catch((error: unknown) => {
        console.error('set energy failed', error);
      });
    }
  }

  function renderDueRowItem(item: DueRowItem, isLast: boolean) {
    if (item.kind === 'action') {
      return <DueRow key={`a-${item.action.actionId}`} action={item.action} isLast={isLast} onToggle={() => toggleAction(item.action)} />;
    }
    if (item.kind === 'build-habit') {
      return (
        <BuildHabitRow
          key={`h-${item.habit.habitId}`}
          habit={item.habit}
          isLast={isLast}
          onToggleDone={() => toggleHabitDone(item.habit)}
          onToggleSkip={() => toggleHabitSkip(item.habit)}
        />
      );
    }
    return (
      <AvoidHabitRow
        key={`h-${item.habit.habitId}`}
        habit={item.habit}
        isLast={isLast}
        onLogClean={() => toggleAvoidClean(item.habit)}
        onLogLapse={() => logAvoidLapse(item.habit)}
      />
    );
  }

  // today-view.md §1.7: banner replaces the kicker whenever a non-today date
  // is being viewed; undefined (not just a boolean) so TodayHeader can tell
  // "today, render the ordinary kicker" apart from "backfilling" cleanly.
  const backfillBanner = isBackfilling
    ? { date: activeDate, onBackToToday: () => setViewedDate(null) }
    : undefined;

  const moodEnergyRow = (
    <MoodEnergyRow
      mood={page.mood}
      energy={page.energy}
      onSetMood={setMoodValue}
      onSetEnergy={setEnergyValue}
    />
  );

  // today-phone-parity.md §3: header-adjacent, right after the pulse-line,
  // above the mood/energy row — same placement in every branch of this
  // component (the pinned-objectives strip is unrelated to whether any
  // due/next-up content exists today).
  const pinnedChipStrip = <PinnedObjectiveChipStrip onOpenObjective={onOpenObjective} />;

  // today-phone-parity.md §4: the six-card content stack, inserted after
  // Keeping-in-check/before the footer. Collapsed by construction (no card
  // expanded by default, PRD OQ22's default assumption) — a bounded, small
  // height addition regardless of branch.
  const contentCardStack = (
    <section aria-label="More">
      <MorningNoteSleepCardPhone
        note={page.note}
        sleepHours={page.sleepHours}
        onSetNote={(next) => void api.setNote(pageDate, next).then(() => void load())}
        onSetSleepHours={(hours) => void api.setSleepHours(pageDate, hours).then(() => void load())}
        isLast={false}
      />
      <GratitudeCardPhone
        gratitude={page.gratitude}
        onSetSlot={(slot, text) => void api.setGratitudeSlot(pageDate, slot, text).then(() => void load())}
        isLast={false}
      />
      <PrioritiesCardPhone
        date={pageDate}
        priorities={priorities}
        objectives={objectives}
        onChanged={() => void load()}
        onOpenObjective={onOpenObjective}
        isLast={false}
      />
      <BrainDumpCardPhone
        notes={notes}
        objectives={objectives}
        priorityCount={priorities.length}
        onChanged={() => void load()}
        isLast={false}
      />
      <FavoriteThingCardPhone
        favoriteMoment={page.favoriteMoment}
        onSave={(input) => void api.setFavoriteMoment(pageDate, input).then(() => void load())}
        isLast={true}
      />
    </section>
  );

  if (nothingAtAll) {
    return (
      <main aria-label="Today">
        <TodayHeader dueCount={0} percentDone={100} backfillBanner={backfillBanner} />
        <SentenceOfDayCardPhone />
        {pinnedChipStrip}
        {moodEnergyRow}
        {offline && <OfflineChip />}
        <EmptyToday onCreateObjective={onCreateObjective} onCreateHabit={onOpenHabits} />
        <div style={{ marginTop: 'var(--space-5)' }}>{contentCardStack}</div>
        <TodayFooter
          reward={page.reward}
          rewardConfig={rewardConfig}
          onCreateObjective={onCreateObjective}
          onOpenRewards={onOpenRewards}
          onOpenHabits={onOpenHabits}
          onOpenWeek={onOpenWeek}
          onOpenObjectives={onOpenObjectives}
          onOpenHistory={onOpenHistory}
          isBackfilling={isBackfilling}
          today={initialToday}
          onSelectBackfillDate={(date) => setViewedDate(date === initialToday ? null : date)}
        />
        {celebrationOverlays}
      </main>
    );
  }

  return (
    <main aria-label="Today" style={{ paddingBottom: 'var(--space-8)' }}>
      <TodayHeader
        dueCount={totalDueCount}
        percentDone={computePercent(page)}
        completedToday={page.completedToday}
        totalConsideredToday={page.totalConsideredToday}
        backfillBanner={backfillBanner}
      />
      <SentenceOfDayCardPhone />

      {pinnedChipStrip}

      {moodEnergyRow}

      {offline && <OfflineChip />}

      {dueRowItemsShown.length > 0 && (
        <section style={{ padding: 'var(--space-5) var(--layout-page-margin-phone) 0' }}>
          <SectionLabel>Due today</SectionLabel>
          <div>
            {dueRowItemsShown.map((item, i) =>
              renderDueRowItem(item, i === dueRowItemsShown.length - 1),
            )}
          </div>
        </section>
      )}

      <section style={{ padding: 'var(--space-5) var(--layout-page-margin-phone) var(--space-5)' }}>
        <SectionLabel>Next up</SectionLabel>
        {nextUp ? (
          <NextUpCard
            key={nextUp.actionId}
            action={nextUp}
            onToggle={() => toggleAction(nextUp)}
            onOpenObjective={() => onOpenObjective(nextUp.objectiveId)}
          />
        ) : dueRowItemsShown.length === 0 ? (
          <p
            style={{
              fontSize: 'var(--type-body-size)',
              lineHeight: 'var(--type-body-line)',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              margin: 0,
            }}
          >
            Nothing queued yet — pick a next step from an objective.
          </p>
        ) : null}
      </section>

      {keepingInCheckShown.length > 0 && (
        <section style={{ padding: '0 var(--layout-page-margin-phone) var(--space-5)' }}>
          <SectionLabel>Keeping in check</SectionLabel>
          <div>
            {keepingInCheckShown.map((habit, i) => (
              <AvoidHabitRow
                key={habit.habitId}
                habit={habit}
                isLast={i === keepingInCheckShown.length - 1 && keepingInCheckOverflow === 0}
                onLogClean={() => toggleAvoidClean(habit)}
                onLogLapse={() => logAvoidLapse(habit)}
              />
            ))}
          </div>
          {keepingInCheckOverflow > 0 && (
            <p
              style={{
                fontSize: 'var(--type-caption-size)',
                color: 'var(--text-muted)',
                margin: 'var(--space-2) 0 0',
              }}
            >
              +{keepingInCheckOverflow} more
            </p>
          )}
        </section>
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>{contentCardStack}</div>

      <TodayFooter
        reward={page.reward}
        rewardConfig={rewardConfig}
        onCreateObjective={onCreateObjective}
        onOpenRewards={onOpenRewards}
        onOpenHabits={onOpenHabits}
        onOpenWeek={onOpenWeek}
        onOpenObjectives={onOpenObjectives}
        onOpenHistory={onOpenHistory}
        isBackfilling={isBackfilling}
        today={initialToday}
        onSelectBackfillDate={(date) => setViewedDate(date)}
      />
      {celebrationOverlays}
    </main>
  );
}

function neutralSnapshot(): RewardSnapshot {
  return { xpTotal: 0, level: 1, xpIntoCurrentLevel: 0, xpRequiredForNextLevel: 100, streakDays: 0 };
}

/**
 * Resolves a BadgeId to its BadgeView via listBadges() before rendering the
 * earn sheet — the celebration queue only carries the id (CelebrationCue's
 * contracted shape), so the sheet's fuller display (criterion sentence,
 * earned date) needs one extra fetch. Renders nothing while loading rather
 * than a skeleton — the earn sheet is a brief, already-triggered moment, not
 * a navigable screen with its own loading state.
 */
function BadgeEarnSheetLoader({
  badgeId,
  onDismiss,
}: {
  readonly badgeId: string;
  readonly onDismiss: () => void;
}) {
  const api = useDomainApi();
  const [badge, setBadge] = useState<Awaited<ReturnType<typeof api.listBadges>>[number] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listBadges().then((badges) => {
      if (cancelled) return;
      setBadge(badges.find((b) => b.badgeId === badgeId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [api, badgeId]);

  if (!badge) return null;
  return <BadgeEarnSheet badge={badge} onDismiss={onDismiss} />;
}

/**
 * Bug fix (owner hands-on testing: ring showed "0%" and header showed
 * "0 due" while an incomplete, undated action was visibly sitting under
 * NEXT UP). Root cause: this function was a stub that always returned 0 —
 * `DailyPageView.totalConsideredToday`/`completedToday` were added to the
 * contract earlier this session specifically to give the ring a real
 * denominator/numerator (their doc comments on domain-api.ts say so
 * explicitly) but nothing here ever read them. Guards divide-by-zero per
 * that doc comment (0/0 renders 0%, not NaN).
 *
 * Separately, `dueCount` in the header ("N due") is intentionally scoped to
 * DATED due-today items only (today-view.md's "due today" section is
 * date-scoped) — an undated action correctly shows only under NEXT UP, not
 * in that count. That half was correct, just confusing next to a ring stuck
 * at 0%; fixing the ring's real bug resolves the apparent disagreement.
 */
function computePercent(page: DailyPageView): number {
  if (page.totalConsideredToday <= 0) return 0;
  return Math.round((page.completedToday / page.totalConsideredToday) * 100);
}

function TodayHeader({
  dueCount,
  percentDone,
  completedToday,
  totalConsideredToday,
  backfillBanner,
}: {
  readonly dueCount: number;
  readonly percentDone: number;
  /**
   * today-view.md §1.2 v2.7: when supplied, the ring prints the real
   * "completed/total" count instead of a bare percentage -- omit both to
   * fall back to the percent label (used by the "nothing considered
   * today" empty state, where a 0/0 fraction would read as confusing
   * rather than clarifying).
   */
  readonly completedToday?: number;
  readonly totalConsideredToday?: number;
  /**
   * today-view.md §1.7: while viewing a non-today date, the persistent
   * banner REPLACES the kicker row entirely -- the two must never be
   * visible at once, or a user skimming quickly could read the wrong one.
   */
  readonly backfillBanner?: { readonly date: IsoDate; readonly onBackToToday: () => void };
}) {
  return (
    <header style={{ padding: 'var(--space-5) var(--layout-page-margin-phone) 0' }}>
      {backfillBanner ? (
        <BackfillBanner date={backfillBanner.date} onBackToToday={backfillBanner.onBackToToday} />
      ) : (
        <div
          style={{
            fontSize: 'var(--type-label-size)',
            lineHeight: 'var(--type-label-line)',
            letterSpacing: 'var(--type-label-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          {formatKicker(new Date())}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginTop: 'var(--space-2)',
          gap: 'var(--space-3)',
        }}
      >
        <h1
          style={{
            fontSize: 'var(--type-display-size)',
            lineHeight: 'var(--type-display-line)',
            fontWeight: 'var(--type-display-weight)',
            color: 'var(--text-primary)',
            margin: 0,
            letterSpacing: 'var(--type-display-letter-spacing)',
          }}
        >
          Daily Entry
        </h1>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
          <span
            className="num"
            style={{
              fontSize: 'var(--type-label-size)',
              lineHeight: 'var(--type-label-line)',
              fontWeight: 600,
              color: 'var(--hue-primary)',
            }}
          >
            {dueCount} due
          </span>
          <ProgressRing percent={percentDone} completed={completedToday} total={totalConsideredToday} />
        </div>
      </div>
      <div style={{ marginTop: 'var(--space-4)' }}>
        <PulseLine />
      </div>
    </header>
  );
}

function SectionLabel({ children }: { readonly children: string }) {
  return (
    <div
      style={{
        fontSize: 'var(--type-label-size)',
        lineHeight: 'var(--type-label-line)',
        letterSpacing: 'var(--type-label-tracking)',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
        fontWeight: 600,
        marginBottom: 'var(--space-1)',
      }}
    >
      {children}
    </div>
  );
}

function DueRow({
  action,
  isLast,
  onToggle,
}: {
  readonly action: DueActionView;
  readonly isLast: boolean;
  readonly onToggle: () => void;
}) {
  const done = action.status === 'done';
  const subtitle = action.dueDate
    ? `Due today · ${action.objectiveTitle}`
    : action.objectiveTitle;

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'flex-start',
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <CheckControl
        checked={done}
        onToggle={onToggle}
        label={done ? `${action.title}, completed. Tap to undo.` : `Mark ${action.title} complete`}
      />
      <div>
        <div
          style={{
            fontSize: 'var(--type-body-size)',
            fontWeight: 'var(--type-body-weight-emphasis)',
            // Same-day completions stay in the list rather than vanishing
            // (contract's DueActionView.status doc) — dim to muted text,
            // matching ObjectiveDetailScreen's ModuleRow completed treatment,
            // so a done row reads as done without disappearing.
            color: done ? 'var(--text-muted)' : 'var(--text-primary)',
          }}
        >
          {action.title}
        </div>
        <div
          style={{
            fontSize: 'var(--type-caption-size)',
            // Neutral tone regardless of overdue status (today-view.md
            // "neutral overdue rendering" — no alarmed color, no red).
            color: 'var(--text-muted)',
            marginTop: 'var(--space-1)',
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function NextUpCard({
  action,
  onToggle,
  onOpenObjective,
}: {
  readonly action: DueActionView;
  readonly onToggle: () => void;
  readonly onOpenObjective: () => void;
}) {
  // Source of truth is action.status (contract's DueActionView), not local
  // state — mirrors the Undo-bug fix already made on ObjectiveDetailScreen's
  // module rows: re-derive from a fresh fetch after the domain layer's
  // subscribe -> reload cycle, never remember completion locally.
  const done = action.status === 'done';
  const pill = formatRelativeDatePill(action.dueDate);

  // Card hugs its own content (`width: fit-content`, capped at the
  // section's full available width) instead of always stretching edge to
  // edge via `justify-content: space-between` — a very short title ("te")
  // previously left the done-state tint stretched across an almost-empty
  // row, reading as an oversized/boxy tile relative to its tiny text
  // (owner-reported live bug, phase-4 spec DS1). `fit-content` (rather than
  // `inline-flex`) keeps the browser's max-content wrap calculation anchored
  // to the same available width in both states, so the title/subtitle
  // column's wrap point does not shift depending on whether the trailing
  // checkmark is present — the leading (checkbox+text) group carries its
  // own `flexShrink: 0` for the same reason. Net effect: the card's width —
  // and the proportion of tinted background to visible content — now stays
  // consistent regardless of title/objective-name length, matching a
  // normal-length action's shape.
  return (
    <div
      style={{
        display: 'flex',
        width: 'fit-content',
        maxWidth: '100%',
        alignItems: 'center',
        gap: 'var(--space-5)',
        padding: 'var(--space-4)',
        background: done
          ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, var(--surface-raised))'
          : 'var(--surface-raised)',
        border: done
          ? '1px solid color-mix(in srgb, var(--hue-primary-vivid) 35%, var(--border-subtle))'
          : '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        transition: 'background var(--dur-base) var(--ease-standard)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          minWidth: 0,
          maxWidth: '100%',
          flexShrink: 0,
        }}
      >
        <CheckControl
          checked={done}
          onToggle={onToggle}
          label={done ? `${action.title}, completed. Tap to undo.` : `Mark ${action.title} complete`}
        />
        <button
          type="button"
          onClick={onOpenObjective}
          style={{ textAlign: 'left', minWidth: 0, maxWidth: '100%' }}
        >
          <div
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {action.title}
          </div>
          <div
            style={{
              fontSize: 'var(--type-caption-size)',
              color: 'var(--text-muted)',
              marginTop: 'var(--space-1)',
            }}
          >
            {action.objectiveTitle}
          </div>
        </button>
      </div>

      {done ? (
        <span aria-label="Done" style={{ display: 'flex', flex: 'none' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M20 6 9 17l-5-5"
              stroke="var(--hue-primary-vivid)"
              strokeWidth={2.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : pill ? (
        <span
          className="num"
          style={{
            fontSize: 'var(--type-label-size)',
            lineHeight: 'var(--type-label-line)',
            letterSpacing: 'var(--type-label-tracking)',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'var(--hue-primary)',
            border: '1px solid color-mix(in srgb, var(--hue-primary) 40%, transparent)',
            borderRadius: 'var(--radius-pill)',
            padding: '5px 11px',
            flex: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {pill}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Footer restructure (owner's 4th report on this footer — a screenshot
 * showed FOUR flat inline links after the level chip overflowing off the
 * right edge of the viewport, the "+" clipped entirely). Root cause across
 * all four rounds: every new destination just appended another flat link to
 * the same fixed-width row, with no ceiling on how many links can fit — a
 * structural problem no amount of padding/font-size tuning can fix.
 *
 * Fixed per the owner's own prototype reference (design/prototype/index.html
 * Direction 03 "Rail", `.td-footer`): exactly TWO zones — the level chip on
 * the left (unchanged) and ONE element on the right. The prototype's one
 * element is a link ("on the rail →"); this build has four real
 * destinations, so the right slot is a single icon-button menu trigger
 * (`MoreHorizontal`, matching the app's one approved icon set) that opens a
 * compact popover listing all four as menu items. A single fixed-width icon
 * button cannot overflow regardless of how many destinations exist behind
 * it — that fixed width, not smaller text, is what makes this fix durable
 * against a 5th destination being added later.
 */
function TodayFooter({
  reward,
  rewardConfig,
  onCreateObjective,
  onOpenRewards,
  onOpenHabits,
  onOpenWeek,
  onOpenObjectives,
  onOpenHistory,
  isBackfilling,
  today,
  onSelectBackfillDate,
}: {
  readonly reward: RewardSnapshot;
  readonly rewardConfig: RewardConfig;
  readonly onCreateObjective: () => void;
  /**
   * Entry point to the Rewards screen (settings-rewards.md's "How rewards
   * work" + badges + settings). settings-rewards.md SS2.1 explicitly leaves
   * "does the ambient level chip itself become tap-through to Rewards"
   * undecided ("not decided here... a design call") — so the chip stays
   * non-interactive, and Rewards is one of the menu's destinations instead.
   */
  readonly onOpenRewards: () => void;
  /** Entry point to the habit list screen (phase-2b-habits.md HB-15). */
  readonly onOpenHabits: () => void;
  /** Entry point to the Weekly Review screen (phase-2c-weekly-review.md WR-24). */
  readonly onOpenWeek: () => void;
  /** Entry point to the All Objectives overview (phase-2d-management-history.md MC-6). */
  readonly onOpenObjectives: () => void;
  /** Entry point to the Completion History screen (phase-2d-management-history.md MC-15). */
  readonly onOpenHistory: () => void;
  /**
   * today-view.md §1.7/AC-P13: while viewing a backfilled (non-today) date,
   * "New objective" is OMITTED from this menu entirely (not disabled) --
   * lifecycle actions must be structurally impossible to reach from the
   * backfill context, not just backend-blocked.
   */
  readonly isBackfilling: boolean;
  readonly today: IsoDate;
  readonly onSelectBackfillDate: (date: IsoDate) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [backfillPopoverOpen, setBackfillPopoverOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  function closeMenu() {
    setMenuOpen(false);
    setBackfillPopoverOpen(false);
    triggerRef.current?.focus();
  }

  function go(action: () => void) {
    setMenuOpen(false);
    action();
  }

  /**
   * Bug fix (coordinator-verified, reproducible open-race): the previous
   * implementation closed the menu via a full-viewport scrim `<div
   * onClick={onClose}>` mounted in the SAME render that opened the menu
   * (`{menuOpen && <TodayFooterMenu .../>}`). A real tap/click sequence
   * dispatches multiple DOM events (pointerdown, mousedown, pointerup,
   * mouseup, click) in quick succession; the moment `menuOpen` flips true and
   * React commits, the scrim (`position: fixed; inset: 0`) is already
   * present in the DOM and can receive a LATER event in that same physical
   * gesture (e.g. the trailing `click` after the trigger's own `click`
   * already fired `setMenuOpen(true)` mid-gesture, or a synthetic
   * dispatch that fires `click` as its own event after the state commit) —
   * closing the menu again before it ever became visibly, durably open.
   * That is exactly "opens then immediately closes within the same
   * interaction," reproduced independently three separate ways.
   *
   * The correct, standard fix: don't render an always-present, synchronously
   * clickable scrim at all. Attach the outside-click listener via
   * `useEffect` keyed on `menuOpen`, which only registers on the NEXT tick
   * after the open state has committed and the browser has finished the
   * gesture that opened it — the same "register after commit" pattern any
   * correct menu-button implementation needs. The trigger itself is
   * explicitly excluded from the "outside" check via `triggerRef`/`
   * menuRef.contains`, so re-clicking the trigger toggles through its own
   * `onClick`, never double-fires a close from this listener.
   */
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // While the backfill date-list popover has taken over this same anchor
    // (TodayFooterMenu unmounts, so menuRef.current would be null), THAT
    // component owns its own independent outside-click dismissal — this
    // effect must stand down entirely rather than treating every click as
    // "outside" (menuRef.current?.contains would be false for anything, the
    // exact bug this guard prevents: the backfill popover closing itself
    // the instant it opens).
    if (!menuOpen || backfillPopoverOpen) return;

    function handlePointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      closeMenu();
    }

    // Registered on the NEXT tick (not synchronously during the same click
    // that opened the menu) — this is the actual fix. Attaching this
    // listener inline during the opening click's own event-handling phase
    // is what caused the same physical gesture to be seen as "outside."
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);

    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [menuOpen, backfillPopoverOpen]);

  return (
    <footer
      // today-view.md v2.2 SS1.5 / tokens.md `pattern.pageFooter`: PINNED to
      // the bottom of the viewport, not flowed after content with a top
      // margin. The earlier "flowed, margin-top: space-8" build read as
      // "more list, not a footer" and left a large dead-space gap above it
      // on the empty state (the footer had to travel down to meet however
      // little content existed above it). Fixed position anchors the footer
      // to the screen edge unconditionally; the border-top + padding-top
      // below is the "unconditional separation" the pattern specifies,
      // independent of how much content sits above.
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        // Bug fix (owner-reported: footer "floats"/drifts on a real iPhone).
        // A flat `bottom: space-5` never accounted for the home-indicator
        // safe area this PWA already opts into (index.html's
        // `viewport-fit=cover`) -- on a notched iPhone that gap can read as
        // the footer sitting too close to / jittering against the gesture
        // area as Safari's/the standalone shell's chrome reflows. `max()`
        // keeps today's 20px gap on devices with no inset (unchanged
        // desktop-viewport/older-device look) and grows only on devices
        // that report a real safe-area-inset-bottom.
        bottom: 'max(var(--space-5), env(safe-area-inset-bottom))',
        paddingLeft: 'var(--layout-page-margin-phone)',
        paddingRight: 'var(--layout-page-margin-phone)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        background: 'var(--surface-base)',
      }}
    >
      {/* settings-rewards.md SS2.1: the footer's left slot activates the
          real level-identity chip now that Phase 2a's reward engine exists,
          replacing Phase 1's placeholder "X objectives on track" fact-only
          line (that Phase-1 fact is still readable via the objective list
          itself / a future Rewards-tab link — this slot's whole point per
          the spec is to be the level's home, not a second home for the
          objectives count). aria-label states the full numeric fact even
          though the visual is ambient (SS9's "accessibility doesn't inherit
          ambient as an excuse to hide the fact" rule, restated here).
          min-width: 0 lets it truncate/share space gracefully rather than
          push the menu trigger off-screen — the exact failure mode being
          fixed here. */}
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <LevelFooterChip snapshot={reward} config={rewardConfig} />
      </div>

      {/* today-phone-parity.md §5.2: this anchor is ordinarily sized to its
          own content (the 44px MoreHorizontal trigger) — fine for
          TodayFooterMenu's own compact dropdown, but too narrow to host a
          7-column month grid. While the calendar is open, this anchor is
          repositioned relative to the PAGE's own margins (`position: fixed;
          left/right: var(--layout-page-margin-phone)`) instead of staying
          sized to the trigger's own box — the trigger button itself keeps
          its normal document position either way (it's a flex sibling of
          the level chip, unaffected by this wrapper's own position mode),
          so nothing about the trigger's tap target moves. */}
      <div
        style={
          backfillPopoverOpen
            ? {
                position: 'fixed',
                left: 'var(--layout-page-margin-phone)',
                right: 'var(--layout-page-margin-phone)',
                bottom: 'max(var(--space-5), env(safe-area-inset-bottom))',
              }
            : { position: 'relative', flex: 'none' }
        }
      >
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More destinations"
          onClick={() => setMenuOpen((open) => !open)}
          style={{
            width: 'var(--touch-target-min)',
            height: 'var(--touch-target-min)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            background: menuOpen ? 'var(--surface-hover)' : 'transparent',
          }}
        >
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>

        {menuOpen && !backfillPopoverOpen && (
          <TodayFooterMenu
            menuRef={menuRef}
            onClose={closeMenu}
            onOpenHabits={() => go(onOpenHabits)}
            onOpenRewards={() => go(onOpenRewards)}
            onOpenWeek={() => go(onOpenWeek)}
            onOpenObjectives={() => go(onOpenObjectives)}
            onOpenHistory={() => go(onOpenHistory)}
            onCreateObjective={() => go(onCreateObjective)}
            onBackfillADay={() => setBackfillPopoverOpen(true)}
            // AC-P13: "New objective" is OMITTED (not disabled) while
            // viewing a backfilled date -- lifecycle actions must be
            // structurally impossible to reach, not just backend-blocked.
            showCreateObjective={!isBackfilling}
          />
        )}

        {menuOpen && backfillPopoverOpen && (
          // today-phone-parity.md §5.1-5.2 (R8.13): swaps the retired
          // BackfillDatePopover flat list for the same DatePickerPopover
          // calendar desktop's date-jump already uses. The immediate
          // ancestor `<div>` above has already been widened to span the
          // page's own margins (rather than the 44px trigger's own width)
          // for exactly this case, per §5.2's derivation — a 7-column
          // month grid needs real width. `anchor="above"` makes the
          // popover open upward from that ancestor's top edge, landing
          // directly above the footer, exactly where BackfillDatePopover
          // used to sit.
          <DatePickerPopover
            selected={null}
            today={today}
            anchor="above"
            onSelect={(date) => {
              setMenuOpen(false);
              setBackfillPopoverOpen(false);
              onSelectBackfillDate(date as IsoDate);
            }}
            onClose={closeMenu}
          />
        )}
      </div>
    </footer>
  );
}

/**
 * The footer's compact destination menu (Habits, Rewards, Week, New
 * objective), anchored above the trigger since the footer is pinned to the
 * bottom of the viewport (no room to open downward). `role="menu"`/
 * `menuitem` with arrow-key + Escape handling per the standard menu-button
 * pattern, matching this app's other popovers' keyboard-operability bar.
 *
 * No scrim element here (unlike `DatePickerPopover`/`IconPickerPopover`) —
 * see the open-race bug fix note on `TodayFooter`'s `useEffect`: outside-tap
 * dismissal is handled by a `document`-level listener registered on the
 * NEXT tick after open, owned by the parent, rather than an
 * always-present clickable overlay mounted in the same render that opened
 * the menu (which is what caused the close-on-open race).
 */
function TodayFooterMenu({
  menuRef,
  onClose,
  onOpenHabits,
  onOpenRewards,
  onOpenWeek,
  onOpenObjectives,
  onOpenHistory,
  onCreateObjective,
  onBackfillADay,
  showCreateObjective,
}: {
  readonly menuRef: React.RefObject<HTMLDivElement>;
  readonly onClose: () => void;
  readonly onOpenHabits: () => void;
  readonly onOpenRewards: () => void;
  readonly onOpenWeek: () => void;
  readonly onOpenObjectives: () => void;
  readonly onOpenHistory: () => void;
  readonly onCreateObjective: () => void;
  /** today-view.md §1.7: opens the date-list popover in this same anchor. */
  readonly onBackfillADay: () => void;
  /**
   * AC-P13: OMITTED (not disabled/greyed-out) while viewing a backfilled
   * date -- lifecycle actions must be structurally impossible to reach from
   * the backfill context, matching this product's standing "absence, not a
   * dimmed ghost of a forbidden action" pattern.
   */
  readonly showCreateObjective: boolean;
}) {
  const items: readonly {
    readonly label: string;
    readonly onSelect: () => void;
    readonly accent?: boolean;
    readonly icon?: typeof Plus;
  }[] = [
    { label: 'Objectives', onSelect: onOpenObjectives },
    { label: 'Habits', onSelect: onOpenHabits },
    { label: 'Rewards', onSelect: onOpenRewards },
    { label: 'Week', onSelect: onOpenWeek },
    { label: 'History', onSelect: onOpenHistory },
    { label: 'Backfill a day', onSelect: onBackfillADay, icon: CalendarClock },
    ...(showCreateObjective
      ? [{ label: 'New objective', onSelect: onCreateObjective, accent: true, icon: Plus }]
      : []),
  ];

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current[0]?.focus();
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    const count = items.length;
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        itemRefs.current[(current + 1 + count) % count]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        itemRefs.current[(current - 1 + count) % count]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      case 'Tab':
        onClose();
        break;
      default:
        break;
    }
  }

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="More destinations"
      onKeyDown={handleKeyDown}
      style={{
        position: 'absolute',
        zIndex: 41,
        right: 0,
        bottom: 'calc(100% + var(--space-2))',
        minWidth: '190px',
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <button
            key={item.label}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            type="button"
            role="menuitem"
            onClick={item.onSelect}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              color: item.accent ? 'var(--hue-primary)' : 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontWeight: item.accent ? 600 : 500,
              textAlign: 'left',
            }}
          >
            {Icon && <Icon size={14} aria-hidden="true" />}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
